import React, { useState } from 'react';
import { 
  Truck, 
  Receipt, 
  Calendar, 
  MapPin, 
  ChevronRight, 
  Globe,
  Sparkles,
  Store
} from 'lucide-react';

export default function LandingPage({ lang = 'mr', onToggleLang, onNavigate }) {
  const isMr = lang === 'mr';
  const [dairyCode, setDairyCode] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);

  const handleGoToDairy = (e) => {
    e?.preventDefault();
    const slug = dairyCode.trim().toLowerCase() || 'samarth-krupa-dairy';
    window.location.hash = `#${slug}`;
    onNavigate(`tenant_login_${slug}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', color: '#0f172a', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      
      {/* 1. TOP NAVBAR (Bright & Clean) */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0.9rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => onNavigate('landing')}>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.3rem',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
            }}>
              🥛
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#0f172a' }}>
                MilkPro <span style={{ color: '#0284c7' }}>Mitra</span>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>
                {isMr ? 'दूध व डेअरी वितरण सॉफ्टवेअर' : 'Milk & Dairy Retail Distribution SaaS'}
              </div>
            </div>
          </div>

          {/* Nav Right (Only Client-facing controls) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            {/* Language Switcher */}
            <button
              onClick={() => onToggleLang(isMr ? 'en' : 'mr')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                color: '#334155',
                padding: '0.45rem 0.8rem',
                borderRadius: 8,
                fontSize: '0.82rem',
                fontWeight: 800,
                cursor: 'pointer'
              }}
            >
              <Globe size={14} color="#0284c7" />
              <span>{isMr ? 'English' : 'मराठी'}</span>
            </button>

            {/* Direct Dairy Login Button */}
            <button
              onClick={() => setShowCodeModal(true)}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '0.55rem 1.3rem',
                borderRadius: 9,
                fontSize: '0.88rem',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Store size={15} />
              <span>{isMr ? 'डेअरी लॉगिन' : 'Dairy Login'}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* 2. HERO SECTION (Bright Sky & Cream Aesthetics) */}
      <section style={{
        background: 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)',
        padding: '4rem 1.5rem 3.5rem 1.5rem',
        borderBottom: '1px solid #e2e8f0',
        textAlign: 'center',
        position: 'relative'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          
          {/* Tagline Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#e0f2fe',
            border: '1px solid #bae6fd',
            padding: '6px 16px',
            borderRadius: 999,
            fontSize: '0.82rem',
            fontWeight: 800,
            color: '#0369a1',
            marginBottom: '1.5rem'
          }}>
            <Sparkles size={15} color="#0284c7" />
            <span>{isMr ? 'दूध व डेअरी उत्पादनांची विक्री व वितरण सुलभ करणारी आधुनिक प्रणाली' : 'Modern Software for Milk & Dairy Product Sales and Distribution'}</span>
          </div>

          {/* Main Headline */}
          <h1 style={{
            fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
            fontWeight: 900,
            lineHeight: 1.22,
            letterSpacing: '-0.02em',
            margin: '0 0 1.25rem 0',
            color: '#0f172a'
          }}>
            {isMr ? (
              <>
                दूध व डेअरी उत्पादने वितरणासाठी <br />
                <span style={{
                  color: '#0284c7',
                  textDecoration: 'underline',
                  textDecorationColor: '#bae6fd',
                  textUnderlineOffset: '6px'
                }}>
                  वेगवान, सुलभ आणि आधुनिक
                </span> क्लाउड बिलिंग सिस्टीम
              </>
            ) : (
              <>
                Fast, Simple & Modern Cloud Billing <br />
                <span style={{ color: '#0284c7' }}>for Milk & Dairy Retail Networks</span>
              </>
            )}
          </h1>

          {/* Subheading */}
          <p style={{
            fontSize: 'clamp(1rem, 1.8vw, 1.15rem)',
            color: '#475569',
            lineHeight: 1.65,
            maxWidth: 720,
            margin: '0 auto 2.25rem auto'
          }}>
            {isMr 
              ? 'दैनिक घरपोच दूध रतीब वितरण, सुपरफास्ट काऊंटर POS बिलिंग (थर्मल प्रिंट), ३०-दिवसीय ग्राहक कॅलेंडर हिशोब आणि मार्ग व्यवस्थापन — प्रत्येक डेअरीसाठी स्वतःचा स्वतंत्र सुरक्षित डेटाबेस!'
              : 'Daily home delivery run-sheets, 3" thermal POS billing, dispute-free 30-day customer ratib calendars, and route management — backed by dedicated databases for each dairy.'}
          </p>

          {/* Quick Action Button */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowCodeModal(true)}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '0.85rem 2.25rem',
                borderRadius: 12,
                fontSize: '1rem',
                fontWeight: 800,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 8px 20px rgba(2, 132, 199, 0.35)',
                transition: 'all 0.15s'
              }}
            >
              <span>{isMr ? '🚀 डेअरी लॉगिन करा' : '🚀 Dairy Login'}</span>
              <ChevronRight size={18} />
            </button>

            <button
              onClick={() => {
                const el = document.getElementById('features-section');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#334155',
                padding: '0.85rem 1.75rem',
                borderRadius: 12,
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              {isMr ? 'वैशिष्ट्ये पहा' : 'Explore Features'}
            </button>
          </div>

          </div>
      </section>

      {/* 3. CORE 4 PILLARS (Bright Cards) */}
      <section id="features-section" style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '4rem 1.5rem'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {isMr ? 'डेअरी विक्री व वितरण प्रणाली' : 'DAIRY RETAIL SUITE'}
          </span>
          <h2 style={{ fontSize: '1.85rem', fontWeight: 900, color: '#0f172a', margin: '0.4rem 0 0.5rem 0' }}>
            {isMr ? 'दूध व डेअरी व्यवसायाचे ४ प्रमुख आधारस्तंभ' : '4 Pillars of Milk & Dairy Retail Operations'}
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.95rem', margin: 0 }}>
            {isMr ? 'ग्राहकांचे रतीब, दुकानातील काउंटर विक्री आणि वितरण — सर्व काही एकाच प्रणालीत' : 'Retail counter, delivery run-sheets, and monthly ratib ledgers united in one platform'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          
          {/* Card 1 */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.15s, box-shadow 0.15s'
          }}>
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 12,
              background: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Truck size={26} color="#0284c7" />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
              {isMr ? '१. दैनिक घरपोच रतीब वितरण (Run-Sheet)' : '1. Daily Delivery Run-Sheet'}
            </h3>
            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
              {isMr 
                ? 'डिलिव्हरी बॉयसाठी आजच्या तारखेला लॉक केलेली सुलभ रन-शीट. मार्ग (Line) व केंद्र निवड, ग्राहकांचा क्रम, आणि रिअल-टाइम दुधाचे प्रमाण नोंद.'
                : 'Locked to current date for delivery boys. Route ordering, live customer search, and quick delivery confirmations.'}
            </p>
            <div style={{ fontSize: '0.8rem', color: '#0284c7', fontWeight: 800, marginTop: 'auto' }}>
              ✓ {isMr ? 'मोबाईल PWA सपोर्ट (नो इन्स्टॉलेशन)' : 'PWA Mobile Ready'}
            </div>
          </div>

          {/* Card 2 */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 12,
              background: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Receipt size={26} color="#d97706" />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
              {isMr ? '२. सुपरफास्ट काऊंटर POS बिलिंग' : '2. Superfast Counter POS'}
            </h3>
            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
              {isMr 
                ? 'पनीर, तूप, दही, लोणी आणि पॅकिंग दूध विक्रीसाठी F2/F8 कीबोर्ड शॉर्टकट्स, ३" थर्मल प्रिंटर पावती आणि कंपनी बारकोड व सिस्टीम QR कोडचा हायब्रिड सपोर्ट.'
                : 'Keyboard shortcuts (F2/F8), 3" thermal receipts, company barcode & internal system QR codes for fast checkout.'}
            </p>
            <div style={{ fontSize: '0.8rem', color: '#d97706', fontWeight: 800, marginTop: 'auto' }}>
              ✓ {isMr ? 'कीबोर्ड आणि बारकोड स्कॅनर स्नेही' : 'Keyboard & Scanner Optimized'}
            </div>
          </div>

          {/* Card 3 */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 12,
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Calendar size={26} color="#059669" />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
              {isMr ? '३. ३०-दिवसीय मासिक रतीब कॅलेंडर' : '3. 30-Day Monthly Ratib Grid'}
            </h3>
            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
              {isMr 
                ? 'Daily, Alternate Days व On-Demand ग्राहकांचे पूर्ण महिन्याचे कॅलेंडर. सुट्ट्यांची नोंद, ज्यादा दुधाचा हिशोब आणि वाद झाल्यास एडमिनद्वारे त्वरित दुरुस्ती.'
                : 'Editable 30-day monthly grid for regular milk customers. Handles vacation days, extra milk, and dispute correction.'}
            </p>
            <div style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 800, marginTop: 'auto' }}>
              ✓ {isMr ? 'पारदर्शक व वादमुक्त मासिक बिलिंग' : 'Dispute-Free Monthly Bills'}
            </div>
          </div>

          {/* Card 4 */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 12,
              background: '#f3e8ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MapPin size={26} color="#7c3aed" />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>
              {isMr ? '४. लाइन व मार्ग व्यवस्थापन' : '4. Routes & Lines Master'}
            </h3>
            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
              {isMr 
                ? 'डेअरी मालकास स्वतःच्या विक्री केंद्रांखाली अमर्याद वितरण लाइन्स (Routes) तयार करण्याचे अधिकार. प्रत्येक लाइनला डिलिव्हरी बॉयची नेमणूक.'
                : 'Create and organize unlimited distribution routes under centers. Assign specific delivery boys to routes.'}
            </p>
            <div style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 800, marginTop: 'auto' }}>
              ✓ {isMr ? 'डेअरी ॲडमिनद्वारे पूर्ण नियंत्रण' : 'Full Admin Route Control'}
            </div>
          </div>

        </div>
      </section>

      

      

      {/* 6. FOOTER (Clean & Public) */}
      <footer style={{
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        padding: '2.5rem 1.5rem',
        textAlign: 'center',
        fontSize: '0.85rem',
        color: '#64748b'
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            © 2026 <strong>MilkPro Mitra</strong>. {isMr ? 'सर्व हक्क राखीव. दूध व डेअरी विक्री व्यवस्थापन SaaS प्रणाली.' : 'All rights reserved. Milk & Dairy Retail SaaS Platform.'}
          </div>
          <div style={{ display: 'flex', gap: '1.25rem' }}>
            <span style={{ cursor: 'pointer', color: '#0284c7', fontWeight: 700 }} onClick={() => setShowCodeModal(true)}>
              {isMr ? 'डेअरी लॉगिन' : 'Dairy Login'}
            </span>
          </div>
        </div>
      </footer>

      {/* 7. DAIRY SELECTION MODAL (Without leaking client list!) */}
      {showCodeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 18,
            width: '100%',
            maxWidth: 440,
            padding: '2rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                {isMr ? 'डेअरी पोर्टल लॉगिन' : 'Dairy Portal Access'}
              </h3>
              <button
                onClick={() => setShowCodeModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 1.25rem 0', lineHeight: 1.5 }}>
              {isMr 
                ? 'तुमच्या डेअरीचा स्वतंत्र लॉगिन पत्ता उघडण्यासाठी डेअरी कोड प्रविष्ट करा (उदा. samarth-krupa-dairy):' 
                : 'Enter your unique dairy code to access your isolated login page (e.g. samarth-krupa-dairy):'}
            </p>

            <form onSubmit={handleGoToDairy} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  {isMr ? 'डेअरी कोड / URL स्लग' : 'Dairy Code / Slug'}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: '0.35rem 0.75rem' }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>#/</span>
                  <input
                    type="text"
                    required
                    placeholder="samarth-krupa-dairy"
                    value={dairyCode}
                    onChange={e => setDairyCode(e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      width: '100%',
                      padding: '0.45rem',
                      fontSize: '0.9rem',
                      outline: 'none',
                      fontWeight: 700,
                      color: '#0f172a'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                  {isMr ? 'चाचणीसाठी थेट एंटर दाबा किंवा "samarth-krupa-dairy" टाका.' : 'Press Enter or submit for demo dairy.'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowCodeModal(false)}
                  style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.7rem', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1.5,
                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.7rem',
                    borderRadius: 10,
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)'
                  }}
                >
                  {isMr ? 'पोर्टल उघडा →' : 'Open Portal →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
